import { useEffect, useState, useRef } from "react";
import { FormattedMessage } from "react-intl";
import cn from "classnames";

// Mui components
import {
    Box,
    Grid,
    TextField,
    Typography,
    LinearProgress,
    FormGroup,
    FormControlLabel,
    IconButton,
    Alert,
} from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";

// Components
import HelperText from "components/HelperText";
import { LinkButton } from "components/Buttons";
import AutocompleteField, {
    useAutocompleteCompanies,
    useAutocompleteDrxWorkPosition,
} from "components/Autocomplete/AutocompleteField";
import { FieldHintButton } from "components/FieldHintButton";
import CustomIcon from "components/CustomIcon";
import { Checkbox } from "components/Checkbox";

// Hooks
import useCreateYourProfile from "./useCreateYourProfile";

// Styles
import useStyles from "./CreateYourProfile.styles";

// Icons
import AddCircleRoundedIcon from "@mui/icons-material/AddCircleRounded";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";

// Types
import { EntryError, WHEntry } from "../SetupWizard.types";
import { CompanyName, CompanyNameDomains } from "services";
import { TAutocompleteFieldObjectValue } from "components/Autocomplete/AutocompleteField/AutocompleteField.types";

// Utils
import icons from "enums/icons";
import { isEqual } from "lodash";
import { getMinimumDate } from "utils/utils";

export const ProfileEntry = ({
    entry,
    setWHEntries,
    showFieldsInline = true,
    shouldClear = true,
    showHelperText = false,
    entryErrors,
    setEntryErrors,
}: {
    entry: WHEntry;
    setWHEntries: React.Dispatch<React.SetStateAction<WHEntry[]>>;
    showFieldsInline?: boolean;
    shouldClear?: boolean;
    showHelperText?: boolean;
    entryErrors?: EntryError[];
    setEntryErrors?: React.Dispatch<React.SetStateAction<EntryError[]>>;
}) => {
    const classes = useStyles.profileRow({ showFieldsInline });

    const {
        drxWorkPositionsList,
        placeholderDrxWorkPosition,
        setDrxWorkPositionsList,
        getOptionLabelDrxWorkPosition,
        renderOptionDrxWorkPosition,
        handleInputChangeDrxWorkPosition,
    } = useAutocompleteDrxWorkPosition();

    const {
        companiesList,
        placeholderCompany,
        setCompaniesList,
        getOptionLabelCompany,
        renderOptionCompany,
        handleInputChangeCompany,
    } = useAutocompleteCompanies();

    const {
        startDateEndAdornment,
        endDateEndAdornment,
        onInputChangeWorkPosition,
        onChangeWorkPosition,
        onInputChangeCompany,
        onChangeCompany,
        onChangeStartDate,
        onChangeEndDate,
        onRemoveEntry,
    } = useCreateYourProfile(
        entry,
        setWHEntries,
        handleInputChangeCompany,
        handleInputChangeDrxWorkPosition,
        setEntryErrors
    );

    const updateEntryErrors = (field: string, value: any) => {
        if (setEntryErrors) {
            setEntryErrors((prevErrors) => {
                let newError = {} as EntryError;
                const doesErrorExists = prevErrors.find((error) => error.id === entry.id);

                newError = {
                    id: entry.id,
                    company: field === "company" ? !value : false,
                    workPosition: field === "workPosition" ? !value : false,
                    startDate: field === "startDate" ? !value : false,
                    endDate: field === "endDate" ? !value : false,
                };

                if (doesErrorExists) {
                    return prevErrors.map((error) => {
                        if (error.id === entry.id) {
                            return { ...error, [field]: !value };
                        } else {
                            return error;
                        }
                    });
                } else {
                    return [...prevErrors, newError];
                }
            });
        }
    };

    const handleOptionSelectWorkPosition = (value: string | TAutocompleteFieldObjectValue | null, reason?: string) => {
        onChangeWorkPosition(value);
        updateEntryErrors("workPosition", value);
    };

    const handleOptionSelectCompany = (value: string | CompanyName | null, reason?: string) => {
        onChangeCompany(value);
        updateEntryErrors("company", value);
    };

    const handleStartDateChange = (value: Date | null) => {
        onChangeStartDate(value, undefined);
        updateEntryErrors("startDate", value);
    };

    const handleEndDateChange = (value: Date | null) => {
        onChangeEndDate(value, undefined);
        updateEntryErrors("endDate", value);
    };

    const handleRemoveEntry = () => {
        onRemoveEntry();

        if (setEntryErrors) {
            setEntryErrors((prevErrors) => prevErrors.filter((error) => error.id !== entry.id));
        }
    };

    const currentErrorEntry = entryErrors?.find((error) => error.id === entry.id);

    const workPositionError = currentErrorEntry?.workPosition;
    const companyError = currentErrorEntry?.company;
    const startDateError = currentErrorEntry?.startDate;
    const endDateError = currentErrorEntry?.endDate;

    const areAllFieldsEmpty = currentErrorEntry && Object.values(currentErrorEntry).every((value) => value);
    const isAnyPositionEmpty =
        currentErrorEntry &&
        Object.values(currentErrorEntry)
            .slice(1)
            .some((value) => value);
    const isAnyFieldEmpty =
        !entry.companyName ||
        !entry.workPosition ||
        !entry.startDate.value ||
        (!entry.endDate.value && !entry.endDate.isPresent);

    return (
        <Grid
            container
            spacing={1}
            className={cn(classes.root, {
                [classes.missingFields]: showFieldsInline && isAnyPositionEmpty,
                [classes.incompleteProfile]: showFieldsInline && isAnyFieldEmpty,
            })}
        >
            {areAllFieldsEmpty && (
                <Grid item className={classes.warningContainer}>
                    <CustomIcon icon={icons.errorTriangle} sx={{ fontSize: 16, mb: 32 }} />
                </Grid>
            )}
            <Grid item xs={showFieldsInline ? 3 : 12} className={classes.inputContainer}>
                <Box>
                    <Typography className={classes.whEntryText}>
                        <FormattedMessage id="wizard.createYourProfile.whEntry.role" />
                    </Typography>
                    <AutocompleteField
                        id={`entry-${entry.id}-work-position`}
                        blurOnSelect
                        freeSolo
                        showValue
                        openOnlyOnOptionsLoaded
                        {...(showFieldsInline ? { popperWidth: 300 } : {})}
                        renderOption={renderOptionDrxWorkPosition}
                        showEndAdorment={false}
                        disableClearable={false}
                        onInputChangeCallback={onInputChangeWorkPosition}
                        onOptionSelectCallback={handleOptionSelectWorkPosition}
                        inputValue={entry.workPosition}
                        inputSize={showFieldsInline ? "small" : "medium"}
                        options={drxWorkPositionsList}
                        optionsUpdater={setDrxWorkPositionsList}
                        getOptionLabel={getOptionLabelDrxWorkPosition}
                        placeholder={placeholderDrxWorkPosition}
                        helperText={
                            workPositionError ? (
                                <HelperText
                                    message={<FormattedMessage id="wizard.createYourProfile.roleWarning" />}
                                    icon={
                                        <CustomIcon
                                            icon={icons.errorTriangle}
                                            style={{
                                                fontSize: 12,
                                                marginTop: 6,
                                            }}
                                        />
                                    }
                                />
                            ) : undefined
                        }
                        filterOptions={(x) => x}
                        error={workPositionError}
                    />
                </Box>
            </Grid>
            <Grid item xs={showFieldsInline ? 3 : 12} className={classes.inputContainer}>
                <Typography className={classes.whEntryText}>
                    <FormattedMessage id="wizard.createYourProfile.whEntry.companyName" />
                </Typography>
                <AutocompleteField
                    id={`entry-${entry.id}-company`}
                    blurOnSelect
                    freeSolo
                    showValue
                    openOnlyOnOptionsLoaded
                    {...(showFieldsInline ? { popperWidth: 400 } : {})}
                    renderOption={renderOptionCompany}
                    onInputChangeCallback={onInputChangeCompany}
                    onOptionSelectCallback={handleOptionSelectCompany}
                    disableClearable={false}
                    inputValue={entry.companyName}
                    inputSize={showFieldsInline ? "small" : "medium"}
                    options={companiesList}
                    optionsUpdater={setCompaniesList}
                    showEndAdorment={false}
                    getOptionLabel={getOptionLabelCompany}
                    placeholder={placeholderCompany}
                    helperText={
                        companyError ? (
                            <HelperText
                                message={<FormattedMessage id="wizard.createYourProfile.companyWarning" />}
                                icon={
                                    <CustomIcon
                                        icon={icons.errorTriangle}
                                        style={{
                                            fontSize: 12,
                                            marginTop: 6,
                                        }}
                                    />
                                }
                            />
                        ) : undefined
                    }
                    filterOptions={(x: CompanyNameDomains) => x}
                    error={Boolean(companyError)}
                    hintButton={
                        <FieldHintButton>
                            <FormattedMessage id="field.hint.company.search" />
                        </FieldHintButton>
                    }
                />
            </Grid>
            <Grid item xs={showFieldsInline ? 2 : 3} className={classes.inputContainer}>
                <Typography className={classes.whEntryText}>
                    <FormattedMessage id="wizard.createYourProfile.whEntry.startDate" />
                </Typography>
                <DesktopDatePicker
                    views={["year", "month"]}
                    inputFormat="MM/yy"
                    minDate={getMinimumDate(50)}
                    maxDate={entry.endDate.value || new Date()}
                    value={entry.startDate.value}
                    open={entry.startDate.isOpen}
                    onChange={handleStartDateChange}
                    disableFuture
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            autoComplete="off"
                            FormHelperTextProps={{
                                sx: {
                                    marginLeft: "4px",
                                    marginRight: "4px",
                                },
                            }}
                            helperText={
                                startDateError ? (
                                    <HelperText
                                        message={<FormattedMessage id="wizard.createYourProfile.startDateWarning" />}
                                        icon={
                                            <CustomIcon
                                                icon={icons.errorTriangle}
                                                style={{
                                                    fontSize: 12,
                                                    marginTop: 6,
                                                }}
                                            />
                                        }
                                    />
                                ) : null
                            }
                            error={startDateError}
                            onClick={() =>
                                setWHEntries((currentEntries) =>
                                    currentEntries.map((currentEntry, index) =>
                                        currentEntry.id === entry.id
                                            ? {
                                                  ...currentEntry,
                                                  startDate: { ...currentEntry.startDate, isOpen: true },
                                              }
                                            : currentEntry
                                    )
                                )
                            }
                            InputProps={{
                                endAdornment: startDateEndAdornment,
                            }}
                            size="small"
                            className={classes.textFieldPlaceHolder}
                        />
                    )}
                    onClose={() =>
                        setWHEntries((currentEntries) =>
                            currentEntries.map((currentEntry, index) =>
                                currentEntry.id === entry.id
                                    ? {
                                          ...currentEntry,
                                          startDate: { ...currentEntry.startDate, isOpen: false },
                                      }
                                    : currentEntry
                            )
                        )
                    }
                />
            </Grid>
            <Grid item xs={showFieldsInline ? 2 : 3} className={classes.inputContainer}>
                <Typography className={classes.whEntryText}>
                    <FormattedMessage id="wizard.createYourProfile.whEntry.endDate" />
                </Typography>
                <DesktopDatePicker
                    views={["year", "month"]}
                    inputFormat="MM/yy"
                    minDate={entry.startDate.value || getMinimumDate(50)}
                    maxDate={new Date()}
                    value={entry.endDate.value}
                    open={entry.endDate.isOpen}
                    onChange={handleEndDateChange}
                    disableFuture
                    disabled={entry.endDate.isPresent}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            FormHelperTextProps={{
                                sx: {
                                    marginLeft: "4px",
                                    marginRight: "4px",
                                },
                            }}
                            helperText={
                                endDateError ? (
                                    <HelperText
                                        sx={{ paddingRight: "2px", paddingLeft: "2px" }}
                                        message={<FormattedMessage id="wizard.createYourProfile.endDateWarning" />}
                                        icon={
                                            <CustomIcon
                                                icon={icons.errorTriangle}
                                                style={{
                                                    fontSize: 12,
                                                    marginTop: 6,
                                                }}
                                            />
                                        }
                                    />
                                ) : null
                            }
                            error={endDateError}
                            onClick={() =>
                                !entry.endDate.isPresent
                                    ? setWHEntries((currentEntries) =>
                                          currentEntries.map((currentEntry, index) =>
                                              currentEntry.id === entry.id
                                                  ? {
                                                        ...currentEntry,
                                                        endDate: { ...currentEntry.endDate, isOpen: true },
                                                    }
                                                  : currentEntry
                                          )
                                      )
                                    : null
                            }
                            size="small"
                            className={classes.textFieldPlaceHolder}
                            InputProps={{
                                endAdornment: endDateEndAdornment,
                            }}
                        />
                    )}
                    onClose={() =>
                        setWHEntries((currentEntries) =>
                            currentEntries.map((currentEntry, index) =>
                                currentEntry.id === entry.id
                                    ? {
                                          ...currentEntry,
                                          endDate: { ...currentEntry.endDate, isOpen: false },
                                      }
                                    : currentEntry
                            )
                        )
                    }
                />
                <FormGroup>
                    <FormControlLabel
                        label="Present"
                        className={classes.checkboxLabel}
                        control={
                            <Checkbox
                                size="small"
                                checked={Boolean(entry.endDate.isPresent)}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                    const value = event.target.checked;
                                    updateEntryErrors("endDate", value);
                                    setWHEntries((currentEntries) =>
                                        currentEntries.map((currentEntry) =>
                                            currentEntry.id === entry.id
                                                ? {
                                                      ...currentEntry,
                                                      endDate: {
                                                          ...currentEntry.endDate,
                                                          value: null,
                                                          isPresent: value,
                                                      },
                                                  }
                                                : currentEntry
                                        )
                                    );
                                }}
                            />
                        }
                    />
                </FormGroup>
            </Grid>

            {shouldClear && (
                <Grid item sx={{ width: 30 }}>
                    <IconButton aria-label="remove" onClick={handleRemoveEntry} className={classes.deleteButton}>
                        <DeleteOutlinedIcon />
                    </IconButton>
                </Grid>
            )}
        </Grid>
    );
};

const CreateYourProfile = ({
    whEntries,
    onClickAdd,
    setWHEntries,
    wrapperRef,
    profileExists,
    entryErrors,
    setEntryErrors,
    showAlert,
    setShowAlert,
}: {
    whEntries: WHEntry[];
    onClickAdd: () => void;
    setWHEntries: React.Dispatch<React.SetStateAction<WHEntry[]>>;
    wrapperRef: React.RefObject<HTMLDivElement>;
    profileExists?: boolean;
    entryErrors: EntryError[];
    setEntryErrors: React.Dispatch<React.SetStateAction<EntryError[]>>;
    showAlert: boolean;
    setShowAlert: (value: boolean) => void;
}) => {
    const classes = useStyles.createYourProfile({ showAlert });

    const whEntriesCopy = useRef<WHEntry[]>([]);
    const showDeleteButton = whEntries.length > 1;
    const [alertMessageType, setAlertMessageType] = useState<"defaultMessage" | "uniqueErrorMessage">("defaultMessage");

    useEffect(() => {
        const emptyRowsCount = (errors: EntryError[]) => {
            const entries = errors;
            let count = 0;

            for (const entry of entries) {
                if (entry.company || entry.workPosition || entry.startDate || entry.endDate) {
                    count += 1;
                }
                if (count > 1) {
                    return count;
                }
            }
            return count;
        };

        if (entryErrors.length) {
            const rowsError = emptyRowsCount(entryErrors);
            const wasAnEntryChanged = !isEqual(whEntries, whEntriesCopy.current);

            if (wasAnEntryChanged && whEntriesCopy.current.length) {
                whEntriesCopy.current = [];
                setShowAlert(false);
            } else {
                if (rowsError > 0) {
                    whEntriesCopy.current = whEntries;
                    setAlertMessageType(
                        rowsError === 1 && whEntries.length === 1 ? "uniqueErrorMessage" : "defaultMessage"
                    );
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entryErrors, whEntries]);

    return (
        <>
            <Box className={classes.createProfileWrapper}>
                <Typography className={classes.title}>
                    {profileExists ? (
                        <FormattedMessage id="wizard.finishYourProfile.title" />
                    ) : (
                        <FormattedMessage id="wizard.createYourProfile.title" />
                    )}
                </Typography>
                <LinearProgress variant="determinate" value={50} />
                <Box className={classes.infoContainer}>
                    <Typography className={classes.text}>
                        {profileExists ? (
                            <FormattedMessage id="wizard.finishYourProfile.isYou01" />
                        ) : (
                            <FormattedMessage id="wizard.createYourProfile.notYou01" />
                        )}
                    </Typography>
                    <Typography className={classes.text}>
                        {profileExists && <FormattedMessage id="wizard.finishYourProfile.isYou02" />}
                    </Typography>
                    <Typography className={classes.startedText}>
                        <FormattedMessage id="wizard.createYourProfile.notYou03" />
                    </Typography>
                </Box>
            </Box>

            <Box className={classes.boxWrapper} mb={1}>
                {showAlert && (
                    <Alert
                        iconMapping={{ warning: <CustomIcon icon={icons.errorTriangle} /> }}
                        variant="filled"
                        severity="warning"
                        action={
                            <IconButton size="small" onClick={() => setShowAlert(false)} sx={{ alignSelf: "center" }}>
                                <CustomIcon icon={icons.close} sx={{ color: "common.white", fontSize: 12 }} />
                            </IconButton>
                        }
                    >
                        {alertMessageType === "uniqueErrorMessage" ? (
                            <FormattedMessage id="wizard.createYourProfile.error2" />
                        ) : (
                            <FormattedMessage id="wizard.createYourProfile.error1" />
                        )}
                    </Alert>
                )}
            </Box>

            <Box className={classes.boxWrapper}>
                <Box className={classes.whEntriesWrapper} ref={wrapperRef}>
                    {whEntries.map((entry) => (
                        <ProfileEntry
                            key={entry.id}
                            entry={entry}
                            setWHEntries={setWHEntries}
                            shouldClear={showDeleteButton}
                            entryErrors={entryErrors}
                            setEntryErrors={setEntryErrors}
                        />
                    ))}
                </Box>
                <LinkButton
                    className={classes.addRoleAction}
                    Icon={<AddCircleRoundedIcon sx={{ fontSize: 16 }} />}
                    label={<FormattedMessage id="wizard.createYourProfile.addRole" />}
                    onClick={onClickAdd}
                />
            </Box>
        </>
    );
};

export default CreateYourProfile;
